import { isOverdue } from "./due-date";
import { IssueCard, IssuePriority, StatusCategory } from "./types";

/**
 * The Suggested lane's rules.
 *
 * Deliberately DETERMINISTIC and client-side: these are derived from cards the board has already
 * loaded, with no new endpoint, no model call and no hidden ranking. A triage lane whose logic a
 * user cannot predict is a lane they stop trusting after the first wrong pick — and "why is this
 * here?" has to be answerable in one short sentence, which is exactly what `reason` is.
 *
 * Everything here is pure so the rules can be tested without a board.
 */

export type SuggestionRule = "overdue" | "unassigned_important" | "blocked" | "stale";

export interface Suggestion {
  card: IssueCard;
  rule: SuggestionRule;
  /** One sentence, shown on the card. Never a score, never "AI thinks". */
  reason: string;
}

export interface SuggestionInput {
  cards: readonly IssueCard[];
  categoryOf: (statusId: string) => StatusCategory;
  dismissedIds: readonly string[];
  now?: Date;
  /** Hard cap — a triage lane longer than the real columns is just a second backlog. */
  limit?: number;
}

const DEFAULT_LIMIT: number = 5;

/** In-progress work untouched for this long is the classic silently-stalled card. */
const STALE_DAYS: number = 14;

const IMPORTANT_PRIORITIES: readonly IssuePriority[] = ["urgent", "high"];

// Lower sorts first. Overdue beats everything: it is the only rule with an external deadline.
const RULE_WEIGHT: Record<SuggestionRule, number> = {
  overdue: 0,
  blocked: 1,
  unassigned_important: 2,
  stale: 3
};

function daysSince(iso: string, now: Date): number {
  const then: number = new Date(iso).getTime();
  if (Number.isNaN(then)) return 0;
  return (now.getTime() - then) / 86_400_000;
}

function ruleFor(card: IssueCard, category: StatusCategory, now: Date): Suggestion | null {
  // Finished work is never suggested — a done column is not a to-do list.
  if (category === "done") return null;

  if (isOverdue(card.dueDate, false, now)) {
    return { card, rule: "overdue", reason: "Past its due date" };
  }
  if (card.isBlocked) {
    return { card, rule: "blocked", reason: "Blocked by another issue" };
  }
  if (!card.assigneeUserId && IMPORTANT_PRIORITIES.includes(card.priority)) {
    return { card, rule: "unassigned_important", reason: "High priority, nobody assigned" };
  }
  if (category === "in_progress" && daysSince(card.updatedAt, now) >= STALE_DAYS) {
    return { card, rule: "stale", reason: `In progress, untouched for ${STALE_DAYS}+ days` };
  }
  return null;
}

export function buildSuggestions(input: SuggestionInput): Suggestion[] {
  const now: Date = input.now ?? new Date();
  const dismissed: Set<string> = new Set(input.dismissedIds);
  const limit: number = input.limit ?? DEFAULT_LIMIT;

  const found: Suggestion[] = [];
  for (const card of input.cards) {
    if (dismissed.has(card.id)) continue;
    const suggestion: Suggestion | null = ruleFor(card, input.categoryOf(card.statusId), now);
    if (suggestion) found.push(suggestion);
  }

  found.sort((a: Suggestion, b: Suggestion) => {
    const byRule: number = RULE_WEIGHT[a.rule] - RULE_WEIGHT[b.rule];
    if (byRule !== 0) return byRule;
    // Within a rule, the oldest due date first, then a stable tiebreak on the key so the lane
    // does not reshuffle between renders.
    const aDue: string = a.card.dueDate ?? "9999-12-31";
    const bDue: string = b.card.dueDate ?? "9999-12-31";
    if (aDue !== bDue) return aDue < bDue ? -1 : 1;
    return a.card.key.localeCompare(b.card.key);
  });

  return found.slice(0, limit);
}

const DISMISS_KEY_PREFIX: string = "kuika:board:suggestions:dismissed:";

// Per board, like the dismissals: one noisy board is a reason to hide ITS lane, not every
// board's. A global switch here and a per-board one below would also be an odd pair to explain.
const HIDDEN_KEY_PREFIX: string = "kuika:board:suggestions:hidden:";

/** Dismissals are per board and per browser — a personal triage state, not shared truth. */
export function getDismissedSuggestions(boardId: string): string[] {
  try {
    const raw: string | null = window.localStorage.getItem(`${DISMISS_KEY_PREFIX}${boardId}`);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry: unknown): entry is string => typeof entry === "string");
  } catch {
    return [];
  }
}

/**
 * Drops ids that no longer name a card the board holds — an issue that was completed, archived or
 * deleted would otherwise sit in this list forever, and the list is what a user's every future
 * dismissal is appended to.
 */
export function pruneDismissedSuggestions(dismissedIds: readonly string[], cards: readonly IssueCard[]): string[] {
  const live: Set<string> = new Set(cards.map((card: IssueCard) => card.id));
  return dismissedIds.filter((id: string) => live.has(id));
}

export function setDismissedSuggestions(boardId: string, ids: readonly string[]): void {
  try {
    window.localStorage.setItem(`${DISMISS_KEY_PREFIX}${boardId}`, JSON.stringify(ids));
  } catch {
    // ignore
  }
}

export function getSuggestionsHidden(boardId: string): boolean {
  try {
    return window.localStorage.getItem(`${HIDDEN_KEY_PREFIX}${boardId}`) === "true";
  } catch {
    return false;
  }
}

export function setSuggestionsHidden(boardId: string, hidden: boolean): void {
  try {
    window.localStorage.setItem(`${HIDDEN_KEY_PREFIX}${boardId}`, hidden ? "true" : "false");
  } catch {
    // ignore
  }
}
